import { useEffect, useState, useCallback } from "react";
import { RestaurantCard, FoodCategory } from "../types";

interface UseRestaurantDataResult {
    allRestaurants: RestaurantCard[] | null;
    filteredRestaurants: RestaurantCard[] | null;
    topBrands: RestaurantCard[] | null;
    foodCategories: FoodCategory[] | null;
    loading: boolean;
    cityName: string;
    setLocation: (lat: number, lng: number, name: string) => void;
}

const useRestaurantData = (): UseRestaurantDataResult => {
    const [allRestaurants, setAllRestaurants] = useState<RestaurantCard[] | null>(null);
    const [filteredRestaurants, setFilteredRestaurants] = useState<RestaurantCard[] | null>(null);
    const [topBrands, setTopBrands] = useState<RestaurantCard[] | null>(null);
    const [foodCategories, setFoodCategories] = useState<FoodCategory[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [cityName, setCityName] = useState<string>("Surat");
    const [lat, setLat] = useState<number>(21.1702401);
    const [lng, setLng] = useState<number>(72.83106070000001);

    const setLocation = useCallback((newLat: number, newLng: number, name: string) => {
        setCityName(name);
        setLat(newLat);
        setLng(newLng);
    }, []);

    // Fetch restaurants when lat/lng changes
    useEffect(() => {
        const fetchRestaurants = async () => {
            setLoading(true);
            try {
                const apiUrl = `https://foodfire.onrender.com/api/restaurants?lat=${lat}&lng=${lng}&page_type=DESKTOP_WEB_LISTING`;
                console.log('Fetching restaurants for:', cityName, 'at', lat, lng);
                const response = await fetch(apiUrl);
                const json = await response.json();

                const cards = json?.data?.cards || [];

                // Extract food categories (What's on your mind)
                const categoryCard = cards.find((card: any) =>
                    card?.card?.card?.id === "whats_on_your_mind"
                );
                const categories = categoryCard?.card?.card?.imageGridCards?.info ||
                    categoryCard?.card?.card?.gridElements?.infoWithStyle?.info || [];
                setFoodCategories(categories);

                // Extract top brands
                const topBrandsCard = cards.find((card: any) =>
                    card?.card?.card?.id === "top_brands_for_you"
                );
                const topBrandsData = topBrandsCard?.card?.card?.gridElements?.infoWithStyle?.restaurants || [];
                setTopBrands(topBrandsData);

                // Extract main restaurant list
                const restaurantListCard = cards.find((card: any) =>
                    card?.card?.card?.id === "restaurant_grid_listing"
                );
                let restaurantList = restaurantListCard?.card?.card?.gridElements?.infoWithStyle?.restaurants || [];

                // If no restaurant_grid_listing, find restaurants from other sources
                if (restaurantList.length === 0) {
                    for (let i = 0; i < cards.length; i++) {
                        const checkData = cards[i]?.card?.card?.gridElements?.infoWithStyle?.restaurants;
                        if (checkData && checkData.length > 0) {
                            restaurantList = checkData;
                            break;
                        }
                    }
                }

                setAllRestaurants(restaurantList);
                setFilteredRestaurants(restaurantList);
            } catch (error) {
                console.error("Error fetching restaurants:", error);
                setAllRestaurants([]);
                setFilteredRestaurants([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRestaurants();
    }, [lat, lng, cityName]);

    return {
        allRestaurants,
        filteredRestaurants,
        topBrands,
        foodCategories,
        loading,
        cityName,
        setLocation
    };
};

export default useRestaurantData;

